import { api } from "./axios";

interface Envelope<T> {
  success: true;
  data: T;
}

export interface ReportSummaryItem {
  label: string;
  value: string | number;
}

export interface ReportData {
  title: string;
  subtitle: string;
  generatedAt: string;
  summary: ReportSummaryItem[];
  columns: string[];
  rows: (string | number)[][];
}

export type ReportType = "daily" | "weekly" | "monthly" | "status" | "employee" | "customer";

export interface ReportParams {
  type: ReportType;
  date?: string;
  month?: string;
  employeeId?: string;
  customerId?: string;
}

function extractFilename(contentDisposition: string | undefined, fallback: string): string {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/);
  return match?.[1] ?? fallback;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const reportApi = {
  async getReport(params: ReportParams): Promise<ReportData> {
    if (params.type === "employee") {
      const res = await api.get<Envelope<ReportData>>(`/reports/employee/${params.employeeId}`);
      return res.data.data;
    }
    if (params.type === "customer") {
      const res = await api.get<Envelope<ReportData>>(`/reports/customer/${params.customerId}`);
      return res.data.data;
    }
    const endpoint = params.type; // daily | weekly | monthly | status
    const res = await api.get<Envelope<ReportData>>(`/reports/${endpoint}`, {
      params: { date: params.date, month: params.month },
    });
    return res.data.data;
  },

  async exportPdf(params: ReportParams): Promise<void> {
    const res = await api.get("/reports/export/pdf", {
      params: {
        type: params.type,
        date: params.date,
        month: params.month,
        employeeId: params.employeeId,
        customerId: params.customerId,
      },
      responseType: "blob",
    });
    triggerDownload(res.data, extractFilename(res.headers["content-disposition"], "report.pdf"));
  },

  async exportExcel(params: ReportParams): Promise<void> {
    const res = await api.get("/reports/export/excel", {
      params: {
        type: params.type,
        date: params.date,
        month: params.month,
        employeeId: params.employeeId,
        customerId: params.customerId,
      },
      responseType: "blob",
    });
    triggerDownload(res.data, extractFilename(res.headers["content-disposition"], "report.xlsx"));
  },
};
